/**
 * Price display and manual-override view (Req 4.4, 4.6, 4.8, 9.2, 9.5, 9.7).
 *
 * Shows the current Cash Bid with its source and UTC timestamp, plus a "not
 * current" stale indicator when a retained value is being served (Req 4.8,
 * 9.2). A manual-override input posts to `POST /api/price/{crop}/override`; it
 * is always available (Req 4.4) and is surfaced with a prominent prompt once the
 * backend reports `promptManualOverride` after 3 consecutive failures (Req 9.5).
 * The view loads each panel independently so the user can keep interacting while
 * price retrieval is pending or unavailable (Req 9.7). The USDA AMS reference
 * (`GET /api/price/{crop}/reference`) is shown when available (Req 4.7).
 */
import type { ApiClient } from "../api/client";
import { RANGES, cropLabel, type Crop } from "../config";
import type { FieldError, PriceView, ReferenceResponse } from "../api/types";
import { el, render } from "../ui/dom";
import { formatPrice2, formatUtc, outcomeLabel, sourceLabel } from "../ui/format";

/** Build the price view for one crop. */
export function priceView(api: ApiClient, crop: Crop): HTMLElement {
  const pricePanel = el("div", { class: "card price-panel" }, [
    el("p", { class: "muted", text: "Loading price…" }),
  ]);
  const overridePanel = el("div", { class: "card override-panel" });
  const referencePanel = el("div", { class: "card reference-panel" });
  const outcomesPanel = el("div", { class: "card outcomes-panel" });

  const root = el("section", { class: "view price-view" }, [
    el("h2", { class: "view-title", text: `${cropLabel(crop)} · price` }),
    pricePanel,
    overridePanel,
    referencePanel,
    outcomesPanel,
  ]);

  // Load panels independently — none blocks the others (Req 9.7).
  void loadPrice();
  void loadReference();
  void loadOutcomes();

  async function loadPrice(): Promise<void> {
    const result = await api.getPrice(crop);
    if (!result.ok) {
      render(
        pricePanel,
        el("p", { class: "form-message is-error", attrs: { role: "alert" } }, [
          "Price is unavailable right now.",
        ])
      );
      // Still offer manual entry so the user is never blocked (Req 4.4, 9.7).
      renderOverride(true);
      return;
    }
    renderPrice(result.data);
    const prompt =
      result.data.available && result.data.promptManualOverride === true;
    renderOverride(prompt || (result.data.available && result.data.stale));
  }

  function renderPrice(view: PriceView): void {
    if (!view.available) {
      render(
        pricePanel,
        el("p", { class: "muted", text: "No cash bid recorded yet." })
      );
      return;
    }
    const children: HTMLElement[] = [
      el("div", { class: "price-headline" }, [
        el("span", { class: "price-big", text: `${formatPrice2(view.price)}/bu` }),
        view.stale ? el("span", { class: "stale-badge", text: "Not current" }) : el("span", {}),
      ]),
      el("p", { class: "value-meta", text: `Source: ${sourceLabel(view.source)}` }),
      el("p", { class: "value-meta", text: `As of ${formatUtc(view.timestampUtc)}` }),
    ];
    if (view.overrideActive) {
      children.push(el("p", { class: "value-meta", text: "A manual override is in effect." }));
    }
    render(pricePanel, ...children);
  }

  function renderOverride(prompt: boolean): void {
    const error = el("p", { class: "field-error", attrs: { role: "alert" } });
    const message = el("p", { class: "form-message", attrs: { "aria-live": "polite" } });
    const input = el("input", {
      type: "number",
      name: "overridePrice",
      class: "field-input",
      placeholder: "Enter cash bid",
      attrs: {
        "aria-label": "Manual override price",
        inputmode: "decimal",
        step: "0.01",
        min: String(RANGES.cashBid.min),
        max: String(RANGES.cashBid.max),
      },
    }) as HTMLInputElement;

    const submit = el("button", { type: "submit", class: "btn btn-primary", text: "Set price" });
    let submitting = false;

    const form = el(
      "form",
      {
        class: "override-form",
        on: {
          submit: (event: Event) => {
            event.preventDefault();
            void apply();
          },
        },
      },
      [
        el("label", { class: "field-label", text: "Manual override price" }),
        input,
        submit,
        error,
        message,
      ]
    );

    async function apply(): Promise<void> {
      if (submitting) return;
      error.textContent = "";
      message.textContent = "";
      message.className = "form-message";

      const value = Number(input.value.trim());
      if (input.value.trim().length === 0 || !Number.isFinite(value)) {
        error.textContent = "Enter a numeric price.";
        return;
      }

      submitting = true;
      submit.setAttribute("disabled", "");
      message.textContent = "Saving…";

      const result = await api.submitOverride(crop, value);

      submitting = false;
      submit.removeAttribute("disabled");

      if (result.ok) {
        input.value = "";
        message.className = "form-message is-info";
        message.textContent = "Override applied.";
        renderPrice(result.data);
        void loadOutcomes();
        return;
      }

      const body = result.body as { fieldError?: FieldError };
      if (result.status === 400 && body.fieldError !== undefined) {
        error.textContent = body.fieldError.message;
        return;
      }
      message.className = "form-message is-error";
      message.textContent = "Could not set the price. Please try again.";
    }

    render(
      overridePanel,
      el("h3", { class: "panel-title", text: "Manual override" }),
      prompt
        ? el("p", {
            class: "prompt-banner",
            attrs: { role: "status" },
            text: "Automated price retrieval is unavailable. Enter the current cash bid manually.",
          })
        : el("p", {
            class: "muted",
            text: "Enter a cash bid manually when the automated price is unavailable.",
          }),
      form
    );
  }

  async function loadReference(): Promise<void> {
    const result = await api.getReference(crop);
    if (!result.ok) {
      render(referencePanel);
      return;
    }
    renderReference(result.data);
  }

  function renderReference(reference: ReferenceResponse): void {
    if (!("price" in reference)) {
      render(
        referencePanel,
        el("h3", { class: "panel-title", text: "USDA AMS reference" }),
        el("p", { class: "muted", text: "No reference value available." })
      );
      return;
    }
    render(
      referencePanel,
      el("h3", { class: "panel-title", text: "USDA AMS reference" }),
      el("p", { class: "value", text: `${formatPrice2(reference.price)}/bu` }),
      el("p", { class: "value-meta", text: `Report date ${reference.reportDate}` }),
      el("p", { class: "value-meta", text: `Retrieved ${formatUtc(reference.timestampUtc)}` })
    );
  }

  async function loadOutcomes(): Promise<void> {
    const result = await api.getOutcomes(crop);
    if (!result.ok) {
      render(outcomesPanel);
      return;
    }
    const rows = result.data.outcomes.map((o) =>
      el("li", { class: "outcome-item" }, [
        el("span", { class: "outcome-type", text: outcomeLabel(o.outcome) }),
        el("span", { class: "value-meta", text: formatUtc(o.timestampUtc) }),
      ])
    );
    render(
      outcomesPanel,
      el("h3", { class: "panel-title", text: "Recent retrieval outcomes" }),
      rows.length > 0
        ? el("ul", { class: "outcome-list" }, rows)
        : el("p", { class: "muted", text: "No retrievals recorded yet." })
    );
  }

  return root;
}
